import CustomTextField from '@/components/CustomTextField/CustomTextField';
import PolicyLogoFooter from '@/components/Footer/PolicyLogoFooter';
import { useRegister } from '@/hooks/card/useRegister';
import { FetchError } from '@/hooks/useApi';
import { cardCallbackPath } from '@/router/Routes';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { deviceType } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import StripePayment from '../Pay/components/StripePayment';
import { isValidRegistrationCode, phoneNumberSchema } from './schemas';

const Card = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registrationCode: registrationCodeFromParams } = useParams();
  const { postRegisterCard, setRegistrationCode, ani, isLoadingAni, queryErrorAni } = useRegister();
  const [error, setError] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [stripePaymentSessionId, setStripePaymentSessionId] = useState<string | null>(null);
  const isLoading = postRegisterCard.isPending || isLoadingAni;

  const validatePhoneNumber = () => {
    const result = phoneNumberSchema.safeParse(phoneNumber);
    const errorMessage = result?.error?.errors[0]?.message;
    return errorMessage;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setError('');
  };

  const register = async () => {
    const registerCardBody = {
      ...(ani && ani.ani ? { registrationCode: registrationCodeFromParams as string } : { ani: phoneNumber }),
      deviceType,
      postPaymentRedirectionUrl: location.origin + '/card/' + cardCallbackPath,
      postPaymentRedirectionFailedUrl: location.origin + '/card/' + cardCallbackPath,
      postPaymentRedirectionCancelUrl: location.origin + '/card'
    };

    postRegisterCard.mutate(registerCardBody, {
      onSuccess: (result) => {
        if (result.payment.redirection?.toLowerCase() === 'stripe') {
          setStripeClientSecret(result.payment.clientSecret ?? null);
          setStripePublishableKey(result.payment.publishableKey ?? null);
          setStripePaymentSessionId(result.paymentSessionId);
          setStripeModalOpen(true);
        } else {
          window.location.href = result.payment.redirection;
        }
      },
      onError: (error: FetchError) => {
        if (error.status === 400) {
          const firstError = error?.errors?.ANI[0];
          const errorMessage = firstError ? t(`page.card.error-step.${firstError}`) : t('errors.something-went-wrong');
          setError(t(errorMessage));
        } else {
          navigate('/card/error', { replace: true });
        }
      }
    });
  };

  const handleRegister = () => {
    const errorMessage = validatePhoneNumber();

    if (errorMessage) {
      setError(t(errorMessage));
      return;
    } else {
      register();
    }
  };

  useEffect(() => {
    if (registrationCodeFromParams) {
      if (isValidRegistrationCode(registrationCodeFromParams)) {
        setRegistrationCode(registrationCodeFromParams);
      } else {
        navigate('/card/error', { replace: true });
      }
    }
  }, [registrationCodeFromParams]);

  useEffect(() => {
    if (ani) {
      if (ani.status?.toLowerCase() === 'ready' || ani.status?.toLowerCase() === 'in_progress') {
        setPhoneNumber(ani.ani);
      } else if (ani.status?.toLowerCase() === 'error' || ani.status?.toLowerCase() === 'expired') {
        navigate('/card/error', { replace: true });
      }
    }

    if (queryErrorAni) {
      navigate('/card/error', { replace: true });
    }
  }, [ani, queryErrorAni]);

  return (
    <Stack gap={4} alignItems={'center'} justifyContent={'center'}>
      <Typography variant='body2'>{t('page.card.title')}</Typography>

      <CustomTextField
        autoFocus
        type='tel'
        disabled={!!registrationCodeFromParams}
        fullWidth
        label={t('common-phrases.phone-number')}
        placeholder={t('page.card.enter-phone-number')}
        value={phoneNumber}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleRegister();
          }
        }}
        error={!!error}
        helperText={error}
        slotProps={{
          htmlInput: {
            maxLength: 15
          }
        }}
      />

      <Button
        fullWidth
        variant='contained'
        color='primary'
        onClick={handleRegister}
        disabled={!phoneNumber || isLoading}
      >
        {isLoading ? (
          <Stack sx={{ p: 0.5 }}>
            <CircularProgress size={13} color='white' />
          </Stack>
        ) : (
          t('buttons.continue')
        )}
      </Button>
      <PolicyLogoFooter />

      <StripePayment
        open={stripeModalOpen}
        clientSecret={stripeClientSecret}
        publishableKey={stripePublishableKey}
        returnUrl={
          stripePaymentSessionId
            ? `${location.origin}/card/${cardCallbackPath}?PaymentSessionId=${stripePaymentSessionId}`
            : null
        }
        onSuccess={() =>
          navigate(`/card/${cardCallbackPath}?PaymentSessionId=${stripePaymentSessionId}`, { replace: true })
        }
        onClose={() => setStripeModalOpen(false)}
        onCancel={() => setStripeModalOpen(false)}
      />
    </Stack>
  );
};

export default Card;
